import React from 'react';
import { X, Play, FileText, Music, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { MediaFile } from '@/services/mediaUploadService';

interface MediaPreviewProps {
  mediaFiles: MediaFile[];
  onRemoveFile: (fileId: string) => void;
}

export function MediaPreview({ mediaFiles, onRemoveFile }: MediaPreviewProps) {
  if (mediaFiles.length === 0) return null;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'image': return <Image className="h-4 w-4" />;
      case 'video': return <Play className="h-4 w-4" />;
      case 'audio': return <Music className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium text-muted-foreground">
        Media Files ({mediaFiles.length})
      </div>
      <div className="grid grid-cols-2 gap-2">
        {mediaFiles.map((mediaFile) => (
          <div key={mediaFile.id} className="relative border border-border rounded-lg p-2">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0">
                {mediaFile.type === 'image' && mediaFile.preview ? (
                  <img
                    src={mediaFile.preview}
                    alt="Preview"
                    className="h-12 w-12 object-cover rounded"
                  />
                ) : (
                  <div className="h-12 w-12 bg-muted rounded flex items-center justify-center">
                    {getFileIcon(mediaFile.type)}
                  </div>
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {mediaFile.file.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatFileSize(mediaFile.file.size)}
                </div>
                
                <div className="flex items-center gap-1 mt-1">
                  <Badge 
                    variant={
                      mediaFile.status === 'ready' ? 'secondary' :
                      mediaFile.status === 'error' ? 'destructive' : 'outline'
                    }
                    className="text-xs"
                  >
                    {mediaFile.status === 'ready' ? 'Ready to send' : 
                     mediaFile.status === 'uploading' ? 'Uploading...' : 
                     mediaFile.status}
                  </Badge>
                  
                  {mediaFile.status === 'uploading' && mediaFile.progress !== undefined && (
                    <div className="flex-1 bg-muted rounded-full h-1">
                      <div 
                        className="bg-primary h-1 rounded-full transition-all"
                        style={{ width: `${mediaFile.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onRemoveFile(mediaFile.id)}
                className="h-6 w-6 p-0 absolute top-1 right-1"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}